import { Component, Input, OnInit, OnDestroy, OnChanges, SimpleChanges, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser, CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { format } from 'date-fns';
import { NgxChartsModule, Color, ScaleType, LegendPosition } from '@swimlane/ngx-charts';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatCardModule } from '@angular/material/card';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { AnalyticsService } from '../../../../services/analytics.service';
import { AnalyticsFilters, ChartData } from '../../../../models/analytics.models';
import { HIRING_RATE_COLOR_SCHEME } from '../../analytics-colors';

/**
 * Hiring Rate Chart Component
 * Displays comprehensive dashboard with hiring metrics, multi-line time series, and detailed breakdowns
 * SSR-safe: Only renders charts in browser context
 */
@Component({
  selector: 'app-hiring-rate-chart',
  standalone: true,
  imports: [CommonModule, NgxChartsModule, MatProgressSpinnerModule, MatCardModule, TranslocoModule],
  templateUrl: './hiring-rate-chart.component.html',
  styleUrls: ['./hiring-rate-chart.component.scss'],
})
export class HiringRateChartComponent implements OnInit, OnDestroy, OnChanges {
  private destroy$ = new Subject<void>();
  private readonly platformId = inject(PLATFORM_ID);
  readonly isBrowser = isPlatformBrowser(this.platformId);

  @Input() filters!: AnalyticsFilters;
  @Input() legendPosition: LegendPosition = LegendPosition.Right;

  // Raw API data storage for re-transformation on language change
  private rawData: any = null;

  // Metric cards data
  totalHired = 0;
  totalApplications = 0;
  hiringRate = 0;
  avgDaysToHire = 0;

  // Chart data
  timeSeriesData: ChartData[] = [];
  categoryData: Array<{ name: string; hired: number; applications: number; rate: number; avgDays: number }> = [];
  positionData: Array<{ name: string; hired: number; applications: number; rate: number; avgDays: number }> = [];
  locationData: Array<{ name: string; hired: number; applications: number; rate: number; avgDays: number }> = [];

  // Time series chart configuration (multi-line)
  showXAxis = true;
  showYAxis = true;
  gradient = false;
  showLegend = true;
  showXAxisLabel = true;
  showYAxisLabel = true;
  animations = true;

  // Color scheme - matches metric card colors
  // Hired: green, Applications: blue, Rate: purple
  timeSeriesColorScheme: Color = HIRING_RATE_COLOR_SCHEME;

  loading = false;
  error: string | null = null;

  constructor(private analyticsService: AnalyticsService, private translocoService: TranslocoService) {}

  ngOnInit(): void {
    // Only load chart data on browser
    if (this.isBrowser && this.filters) {
      this.loadChartData();
    }

    // Subscribe to language changes and re-transform data
    this.translocoService.langChanges$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (this.rawData) {
          this.transformAllData();
        }
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    // Reload data when filters change
    if (changes['filters'] && !changes['filters'].firstChange && this.isBrowser) {
      this.loadChartData();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Load chart data from API
   */
  private loadChartData(): void {
    if (!this.filters) {
      return;
    }

    this.loading = true;
    this.error = null;

    this.analyticsService
      .getHiringRate(this.filters)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data) => {
          // Store raw data for language change re-transformation
          this.rawData = data;
          // Transform all chart data with current language
          this.transformAllData();
          this.loading = false;
        },
        error: (err) => {
          console.error('Chart data error:', err);
          this.error = this.translocoService.translate('analytics.errorLoadChartData');
          this.loading = false;
        },
      });
  }

  /**
   * Transform all chart data with current language
   * Called on initial load and when language changes
   */
  private transformAllData(): void {
    if (!this.rawData) {
      return;
    }

    // Set metric cards
    this.totalHired = this.rawData.totalHired;
    this.totalApplications = this.rawData.totalApplications;
    this.hiringRate = this.rawData.hiringRate;
    this.avgDaysToHire = this.rawData.avgDaysToHire;

    // Transform chart data
    this.timeSeriesData = this.transformTimeSeriesData(this.rawData.timeSeries);
    this.categoryData = this.transformCategoryData(this.rawData.breakdown.byCategory);
    this.positionData = this.transformPositionData(this.rawData.breakdown.byPosition);
    this.locationData = this.transformLocationData(this.rawData.breakdown.byLocation);
  }

  /**
   * Transform time series data to ngx-charts multi-line chart format
   * Creates 3 series: Hired, Applications, Rate (%)
   * Format: [
   *   { name: 'Hired', series: [{ name: 'Jan 01', value: 45 }] },
   *   { name: 'Applications', series: [{ name: 'Jan 01', value: 145 }] },
   *   { name: 'Rate (%)', series: [{ name: 'Jan 01', value: 31 }] }
   * ]
   */
  private transformTimeSeriesData(timeSeries: Array<{ date: string; hired: number; applications: number; rate: number }>): ChartData[] {
    if (!timeSeries || timeSeries.length === 0) {
      return [];
    }

    return [
      {
        name: this.translocoService.translate('analytics.hired'),
        series: timeSeries.map((item) => ({
          name: format(new Date(item.date), 'MMM dd'),
          value: item.hired,
        })),
      },
      {
        name: this.translocoService.translate('analytics.applications'),
        series: timeSeries.map((item) => ({
          name: format(new Date(item.date), 'MMM dd'),
          value: item.applications,
        })),
      },
      {
        name: this.translocoService.translate('analytics.ratePercent'),
        series: timeSeries.map((item) => ({
          name: format(new Date(item.date), 'MMM dd'),
          value: item.rate,
        })),
      },
    ];
  }

  /**
   * Transform category breakdown data
   * categoryName is actually the categoryKey (e.g., "top_management")
   */
  private transformCategoryData(
    categories: Array<{ categoryId: number; categoryName: string; hired: number; applications: number; rate: number; avgDaysToHire: number }>
  ): Array<{ name: string; hired: number; applications: number; rate: number; avgDays: number }> {
    if (!categories || categories.length === 0) {
      return [];
    }

    return categories.map((item) => ({
      name: this.translocoService.translate(`jobCategories.${item.categoryName}`),
      hired: item.hired,
      applications: item.applications,
      rate: item.rate,
      avgDays: item.avgDaysToHire,
    }));
  }

  /**
   * Transform position breakdown data
   * positionName is actually the positionKey (e.g., "manager")
   */
  private transformPositionData(
    positions: Array<{ positionId: number; positionName: string; hired: number; applications: number; rate: number; avgDaysToHire: number }>
  ): Array<{ name: string; hired: number; applications: number; rate: number; avgDays: number }> {
    if (!positions || positions.length === 0) {
      return [];
    }

    return positions.map((item) => ({
      name: this.translocoService.translate(`jobPositions.${item.positionName}`),
      hired: item.hired,
      applications: item.applications,
      rate: item.rate,
      avgDays: item.avgDaysToHire,
    }));
  }

  /**
   * Transform location breakdown data
   */
  private transformLocationData(
    locations: Array<{ city: string; country: string; hired: number; applications: number; rate: number; avgDaysToHire: number }>
  ): Array<{ name: string; hired: number; applications: number; rate: number; avgDays: number }> {
    if (!locations || locations.length === 0) {
      return [];
    }

    return locations.map((item) => {
      // Translate location name if it's a special key like "Others"
      const cityName = item.city === 'Others' ? this.translocoService.translate(`locations.${item.city}`) : item.city;
      const name = item.country ? `${cityName}, ${item.country}` : cityName;
      return {
        name,
        hired: item.hired,
        applications: item.applications,
        rate: item.rate,
        avgDays: item.avgDaysToHire,
      };
    });
  }

  /**
   * Format Y-axis to show only whole numbers
   */
  yAxisTickFormatting(value: number): string {
    return Math.floor(value) === value ? value.toString() : '';
  }

  /**
   * Handle chart select event (optional)
   */
  onSelect(event: any): void {
    console.log('Chart item selected:', event);
  }
}
